import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SFButton from "../button";
import { SFFilterIcon } from "../icons";
import SFSelect from "../select";
import { SFStack } from "../grid";

import classes from "./globalFilters.module.scss";

const transformedFormValue = (filters, clearAll = false) => {
  const filtersVal = {};
  filters.forEach((filter) => {
    filtersVal[filter.name] = {
      values: clearAll ? [] : filter.selectedValues,
      filterType: "set",
    };
  });
  return filtersVal;
};

const GlobalFilters = (props) => {
  const { t } = useTranslation();
  const panelRef = useRef(null);
  const btnRef = useRef();
  const [showFiltersMenu, setShowFiltersMenu] = useState(false);
  const [formValue, setFormValue] = useState(
    transformedFormValue(props.filters.filters)
  );

  useEffect(() => {
    setFormValue(transformedFormValue(props.filters.filters));
  }, [props.filters.filters]);

  useEffect(() => {
    /**
     * Close panel if clicked on outside of panel
     */
    const handleClickOutside = (event) => {
      if (
        (panelRef.current && !panelRef.current.contains(event.target)) &&
        (btnRef.current && !btnRef.current.contains(event.target))
      ) {
        setShowFiltersMenu(false);
      }
    };

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [panelRef]);

  const toggleFiltersContainerHandler = () => {
    setShowFiltersMenu((state) => !state);
  };

  const changeFilterHandler = (filter) => {
    setFormValue((prevState) => ({
      ...prevState,
      [filter.name]: { ...prevState[filter.name], values: filter.value },
    }));
  };

  const clickActionHandler = (clear = false) => {
    if (clear) {
      const clearedFormValue = transformedFormValue(
        props.filters.filters,
        true
      );
      props.onApplyFilters(clearedFormValue);
    } else {
      props.onApplyFilters(formValue);
    }
    setShowFiltersMenu(false);
  };

  return (
    <React.Fragment>
      <div ref={btnRef}>
        <SFButton
          type="button"
          variant="secondary"
          sx={{borderColor: "rgba(0, 0, 0, 0.23)", width: 100}}
          onClick={toggleFiltersContainerHandler}
        >
          <SFFilterIcon />
        </SFButton>
      </div>

      {showFiltersMenu && (
        <div
          //need this id in order to position select popover under it and not in body
          id="global-filters-container"
          className={classes.panel}
          ref={panelRef}
        >
          <div className={classes["filters-container"]}>
            {props.filters.filters.map((filter, index) => {
              return (
                <div key={index} className={classes["select-container"]}>
                  <SFSelect
                    placeholder={filter.displayName}
                    onChangeFilter={changeFilterHandler}
                    selectedValue={filter.selectedValues}
                    values={filter.values}
                    name={filter.name}
                    filterValuesUrl={props.filters.url}
                    multiple
                    optionsWithCheckbox
                    {...(filter.color && { leftBorderColor: filter.color })}
                  />
                </div>
              );
            })}
          </div>
          <SFStack direction="row" spacing={2}>
            <SFButton
              type="button"
              onClick={clickActionHandler.bind(this, false)}
              variant="utility"
            >
              {t("components.globalFilters.add")}
            </SFButton>
            <SFButton
              type="button"
              onClick={clickActionHandler.bind(this, true)}
              variant="text-link"
            >
              {t("components.globalFilters.clear")}
            </SFButton>
          </SFStack>
        </div>
      )}
    </React.Fragment>
  );
};

GlobalFilters.propTypes = {
  onApplyFilters: PropTypes.func,
  filters: PropTypes.shape({
    url: PropTypes.string,
    filters: PropTypes.arrayOf(
      PropTypes.shape({
        displayName: PropTypes.string,
        name: PropTypes.string,
        values: PropTypes.arrayOf(PropTypes.string),
        selectedValues: PropTypes.arrayOf(PropTypes.string),
        color: PropTypes.string,
      }).isRequired
    ),
  }),
};

export default GlobalFilters;
